import { useState } from "react";
import { ChevronDown, BookOpen, Info } from "lucide-react";

const Section = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`logic-section-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        <ChevronDown size={16} className={`text-slate-600 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 py-4 space-y-5 text-[13px] text-slate-700 leading-relaxed">{children}</div>}
    </div>
  );
};

const Widget = ({ name, children }) => (
  <div className="border-l-2 border-[#006837] pl-4 py-1">
    <h4 className="text-[13px] font-bold text-slate-900 mb-2">{name}</h4>
    {children}
  </div>
);

const MetricTable = ({ rows }) => (
  <table className="w-full text-[12px] border border-slate-200 rounded mt-2">
    <thead className="bg-slate-50">
      <tr>
        <th className="text-left px-3 py-1.5 font-semibold text-slate-600 border-b w-[18%]">Metric</th>
        <th className="text-left px-3 py-1.5 font-semibold text-slate-600 border-b w-[28%]">Source Column</th>
        <th className="text-left px-3 py-1.5 font-semibold text-slate-600 border-b">How It's Calculated</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={i} className={i % 2 === 0 ? "" : "bg-slate-50/50"}>
          <td className="px-3 py-1.5 border-b border-slate-100 font-medium">{r[0]}</td>
          <td className="px-3 py-1.5 border-b border-slate-100 font-mono text-[11px] text-blue-700">{r[1]}</td>
          <td className="px-3 py-1.5 border-b border-slate-100">{r[2]}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Note = ({ children }) => (
  <div className="flex gap-2 mt-2 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 text-[11px] text-amber-800">
    <Info size={14} className="flex-shrink-0 mt-0.5 text-amber-500" />
    <div>{children}</div>
  </div>
);

const FilterTag = ({ label }) => (
  <span className="inline-block bg-slate-100 border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded text-[10px] font-medium mr-1 mb-1">{label}</span>
);

const FilterList = ({ filters }) => (
  <div className="mt-2">
    <span className="text-[10px] text-slate-500 font-medium uppercase mr-1">Responds to:</span>
    {filters.map(f => <FilterTag key={f} label={f} />)}
  </div>
);

export default function Logics() {
  return (
    <div className="space-y-4" data-testid="logics-page">
      <div className="flex items-center gap-2 mb-2">
        <BookOpen size={20} className="text-[#006837]" />
        <h1 className="text-lg font-bold text-slate-900">Methodology & Logic Reference</h1>
      </div>
      <p className="text-[13px] text-slate-500 -mt-2 mb-4">
        This page documents how every number on the dashboard is calculated, along with the <strong>source data columns</strong> used.
        All monetary values are displayed in <strong>Crores</strong> (1 Crore = 1,00,00,000).
      </p>

      {/* ─── GLOBAL FILTERS ─── */}
      <Section title="Global Filters & View Modes" defaultOpen>
        <Widget name="How the Financial Year (FY) Is Assigned to a Grant">
          <p>
            A grant's FY is determined by its <strong>project start date</strong> (the date the project begins), not the grant approval date.
          </p>
          <MetricTable rows={[
            ["FY assignment rule", "SOURCE.PROJECT_START_DATE", "If a grant starts between April and March, it belongs to that financial year. E.g., start date 15 June 2024 = FY 2024-25. Start date 10 January 2025 = also FY 2024-25."],
            ["FY date range", "(Derived)", "FY 2024-25 covers 1 April 2024 to 31 March 2025"],
          ]} />
          <Note>
            The FY column is not taken directly from source data. It is computed during data sync using PROJECT_START_DATE. Previously it was based on GRANT_APPROVED_DATE — this was changed to align with how the client tracks financial years.
          </Note>
        </Widget>

        <Widget name="FY Filter (Multi-Select)">
          <p>The FY filter supports <strong>multi-select</strong> across three financial years:</p>
          <MetricTable rows={[
            ["Available options", "(Config)", "FY 2024-25, FY 2025-26, FY 2026-27"],
            ["Default selection", "(Config)", "FY 2025-26 only"],
            ["Behavior", "(Derived)", "When specific FYs are selected, only grants matching those FYs (per view mode logic) are shown. When all 3 FYs are checked, it acts as 'All Years' — no FY filter is sent, returning all grants."],
          ]} />
          <Note>
            The header dynamically shows the specific FY name when only one FY is selected, or 'X FYs selected' for multiple, or 'All Years' when all are selected.
          </Note>
        </Widget>

        <Widget name="View Mode Toggle: New / All Grants / Ongoing">
          <p>The toggle at the top of the dashboard controls which grants are included in <strong>all calculations across all pages</strong>:</p>
          <MetricTable rows={[
            ["All Grants (active)", "SOURCE.PROJECT_START_DATE", "Shows all grants where project_start_date is on or before the end of the selected FY (31 March). E.g., FY 2025-26 → start_date <= 31 Mar 2026. This captures all grants that have been approved up to the end of the FY."],
            ["New Grants (snapshot)", "SOURCE.PROJECT_START_DATE", "Shows only grants whose start date falls within the selected FY range. E.g., FY 2025-26 → start_date >= 1 Apr 2025 AND start_date <= 31 Mar 2026."],
            ["Ongoing Grants (ongoing)", "SOURCE.PROJECT_START_DATE", "All Grants minus New Grants. These are grants that started before the selected FY but are still counted in 'All Grants' because their start date is on or before the FY end."],
          ]} />
          <Note>
            <strong>View Mode is disabled when "All Years" is selected.</strong> When all 3 FYs are checked, the toggle is greyed out and automatically resets to "All Grants", because without a specific FY window the New/Ongoing distinction is not meaningful. Switching between view modes changes every number on every page.
          </Note>
        </Widget>

        <Widget name="Other Global Filters">
          <p>These filters (visible in the top bar) are applied across all pages and widgets:</p>
          <MetricTable rows={[
            ["Theme / Portfolio", "SOURCE.THEME", "Filters grants by their thematic category (e.g., Health, Education, Rural Upliftment)"],
            ["Source of Fund / Trust", "SOURCE.TRUST_NAME", "Filters by the funding trust (e.g., Fund A, Fund B, Fund C)"],
            ["Status", "SOURCE.STATUS", "Dropdown: All, Active, Closed"],
            ["Grantee", "SOURCE.GRANTEE_NAME", "Searchable dropdown with grantee organization names (Title Case display)"],
            ["State (Map click)", "grant_geographies.state", "When you click a state on the map, all data is filtered to that geography"],
            ["District (Map click)", "grant_geographies.district", "When you click a district, data is further filtered to that district"],
          ]} />
          <Note>
            Changing any filter or clicking a different state/district on the map automatically resets the Grants Table search input and the Aspirational Districts checkbox, ensuring all widgets stay synchronized.
          </Note>
        </Widget>

        <Widget name="How Disbursement Is Calculated">
          <p>
            Two types of disbursement are shown on the Executive Overview:
          </p>
          <MetricTable rows={[
            ["Disbursed (FY-scoped)", "PAYMENTS.AMOUNT_PAID, PAID_DATE", "Sum of payments where PAID_DATE falls within the selected FY and PAYMENT_STATUS is 'Paid' or 'Refunded'. Only payments made during the selected FY are counted."],
            ["Disbursement (Cumulative)", "PAYMENTS.AMOUNT_PAID", "Sum of ALL payments (Paid + Refunded status) for the filtered grants, regardless of when the payment was made. No FY date filter on paid_date — this is the lifetime total disbursement."],
            ["Payment date filter", "PAYMENTS.PAID_DATE", "Used to determine which FY a payment belongs to for the FY-scoped card. A payment on 15 Feb 2025 counts in FY 2024-25."],
            ["Payment status filter", "PAYMENTS.PAYMENT_STATUS", "Only payments with status 'Paid' or 'Refunded' (case-insensitive) are included in both disbursement totals"],
          ]} />
          <Note>
            The FY-scoped disbursement applies to all view modes. Even if a grant is in scope, only its payments made during the selected FY are counted for that card. The Cumulative card ignores the FY date filter entirely — it sums all historical payments for the filtered grants.
          </Note>
        </Widget>

        <Widget name="Geographic Apportionment (Multi-State Grants)">
          <p>
            When a state or district is selected on the map, grants that operate across multiple states have their financial amounts <strong>proportionally split</strong>.
            This ensures KPI cards, charts, and the heatmap all show consistent numbers for the selected geography.
          </p>
          <MetricTable rows={[
            ["Single-state grant", "grant_geographies", "100% of sanctioned and disbursed amounts attributed to that state"],
            ["Multi-state (blocks available)", "grant_geographies.block", "Split by the ratio of blocks in the selected state vs. total blocks for that grant. E.g., 20 blocks in State A out of 30 total = State A gets 66.7%"],
            ["Multi-state (districts only)", "grant_geographies.district", "Split by the ratio of districts in the selected state vs. total districts"],
            ["Multi-state (no detail)", "(Equal split)", "Split equally across all states (e.g., 3 states = 33.3% each)"],
          ]} />
          <Note>
            The same proportional weights are applied to both sanctioned and disbursement amounts across all widgets. KPI cards display a "~est." label when apportioned values are shown.
          </Note>
        </Widget>

        <Widget name="Empty State Behavior">
          <p>
            When the combination of filters and geography results in <strong>0 matching grants</strong>, all charts display a "No data for selected filters" message instead of showing stale or misleading data from a previous selection.
          </p>
        </Widget>
      </Section>

      {/* ─── EXECUTIVE OVERVIEW ─── */}
      <Section title="Executive Overview Page">
        <Widget name="Grants Approved / Grantees (KPI Cards)">
          <MetricTable rows={[
            ["Grants count", "SOURCE.GRANT_OR_REQUEST", "Count of unique Grant IDs matching the selected FY, view mode, and all active filters"],
            ["Grantees count", "SOURCE.GRANTEE_NAME", "Count of unique grantee organization names across those grants"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "State", "District"]} />
        </Widget>

        <Widget name="Sanctioned Amount (KPI Card)">
          <MetricTable rows={[
            ["Sanctioned (Cr)", "SOURCE.AMOUNT_RECOMMENDED", "Sum of the approved/recommended amount for all grants matching the current filters, converted to Crores"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "State", "District"]} />
        </Widget>

        <Widget name="Disbursed Amount & Cumulative (KPI Cards)">
          <p>The Financial section shows a 2x2 grid: Sanctioned | Disbursement (Cumulative) / Disbursed | Balance Commitment.</p>
          <MetricTable rows={[
            ["Disbursed (Cr)", "PAYMENTS.AMOUNT_PAID", "Sum of all payments where PAYMENT_STATUS = 'Paid' or 'Refunded' and PAID_DATE falls within the selected FY, for grants matching the current filters. Converted to Crores."],
            ["Disbursement (Cumulative)", "PAYMENTS.AMOUNT_PAID", "Sum of ALL payments (Paid + Refunded) for the filtered grants, with no FY date filter on paid_date. This is the lifetime total."],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "State", "District"]} />
        </Widget>

        <Widget name="Disbursement Rate">
          <MetricTable rows={[
            ["Disbursement Rate (%)", "(Derived)", "Disbursed Amount divided by Sanctioned Amount, multiplied by 100. E.g., Sanctioned = 100 Cr and Disbursed = 43 Cr, rate = 43%"],
          ]} />
          <p className="text-[12px] text-slate-500 mt-1">This is a derived metric — not a separate source data column.</p>
        </Widget>

        <Widget name="Geographic Summary (States / Districts / Blocks / Aspirational Districts)">
          <MetricTable rows={[
            ["States & UTs covered", "grant_geographies.state", "Count of distinct Indian states/UTs where filtered grants have a presence (Pan-India tagged grants are excluded from the state count)"],
            ["Districts covered", "grant_geographies.district", "Count of distinct districts across all filtered grants"],
            ["Aspirational Districts", "aspirational_blocks.district_name", "How many of the 112 government-identified Aspirational Districts are covered by the filtered grants (matched by district name, case-insensitive)"],
            ["Aspirational Blocks", "aspirational_blocks.block_name", "How many of the 499 government-identified Aspirational Blocks are covered"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "State", "District"]} />
          <Note>
            Geography data is parsed from the raw SOURCE.GEOGRAPHY column during data sync. Each grant's geography string is broken into individual state/district/block rows.
          </Note>
        </Widget>

        <Widget name="India Map (Heatmap)">
          <p>
            The map colours each state by its <strong>proportional share of sanctioned amount</strong>.
            Darker shades indicate higher allocation.
          </p>
          <MetricTable rows={[
            ["State allocation", "SOURCE.AMOUNT_RECOMMENDED + grant_geographies", "For single-state grants, the full sanctioned amount goes to that state. For multi-state grants, it's split proportionally (see apportionment logic)."],
            ["Grants in state", "grant_geographies.state", "Count of grants with geography entries in that state"],
            ["Disbursement in state", "PAYMENTS.AMOUNT_PAID", "Same proportional weight applied to disbursement per grant"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee"]} />
        </Widget>

        <Widget name="Map Click Drill-Down (State to Districts to Blocks)">
          <MetricTable rows={[
            ["Clicking a state", "grant_geographies.state", "Filters all dashboard data to grants operating in that state. Map zooms to show district-level breakdown."],
            ["Clicking a district", "grant_geographies.district", "Further filters data to that specific district and shows block-level detail"],
            ["Aspirational flag", "aspirational_blocks", "Districts/blocks in the government's Aspirational list are visually highlighted on the map"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "Selected State"]} />
        </Widget>

        <Widget name="Sanctioned by Theme (Pie/Donut Chart)">
          <MetricTable rows={[
            ["Each slice", "SOURCE.THEME, SOURCE.AMOUNT_RECOMMENDED", "Total sanctioned amount for grants in each theme, shown as a percentage of the overall sanctioned amount"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "State (map)", "District (map)"]} />
          <Note>
            When a state or district is selected on the map, the pie chart amounts are geographically apportioned.
          </Note>
        </Widget>

        <Widget name="YoY FY-wise Sanctioned & Disbursement (Bar Chart)">
          <p>Shows sanctioned and disbursed amounts across financial years as a grouped bar chart. Only shows FY 2024-25 onward.</p>
          <MetricTable rows={[
            ["Sanctioned bar (per FY)", "SOURCE.AMOUNT_RECOMMENDED", "In 'New' mode: sum for grants with start_date within each FY. In 'All Grants' mode: sum for grants where start_date is on or before each FY's end. In 'Ongoing' mode: All Grants minus New for each FY."],
            ["Disbursement bar (per FY)", "PAYMENTS.AMOUNT_PAID, PAID_DATE", "Sum of payments actually made in each FY, based on PAID_DATE — but only for grants that are in scope for that FY per the view mode."],
          ]} />
          <FilterList filters={["View Mode", "Theme", "Trust", "Status", "Grantee", "State (map)", "District (map)"]} />
          <Note>
            Each FY bar independently scopes its grants using the selected view mode. When a state/district is selected, geographic apportionment weights are applied. The chart shows "No data" when there are 0 grants.
          </Note>
        </Widget>

        <Widget name="YoY / QoQ Toggle (Line Charts)">
          <p>Two chart modes toggled by the YoY/QoQ switch, showing FY 2024-25 onward only:</p>
          <MetricTable rows={[
            ["YoY Sanctioned (per FY)", "SOURCE.AMOUNT_RECOMMENDED", "Total sanctioned per FY based on project_start_date (in 'New' mode) or date overlap (in 'All Grants' mode)"],
            ["YoY Disbursed (per FY)", "PAYMENTS.AMOUNT_PAID, PAID_DATE", "Sum of payments made in each FY, scoped by view mode"],
            ["QoQ Sanctioned (per Quarter)", "SOURCE.AMOUNT_RECOMMENDED", "Sanctioned amount grouped by quarter of project_start_date. Quarters: Q1 = Apr-Jun, Q2 = Jul-Sep, Q3 = Oct-Dec, Q4 = Jan-Mar"],
            ["QoQ Disbursed (per Quarter)", "PAYMENTS.AMOUNT_PAID, PAID_DATE", "Sum of payments grouped by quarter of paid_date"],
          ]} />
          <FilterList filters={["View Mode", "Theme", "Trust", "Status", "Grantee", "State (map)", "District (map)"]} />
          <Note>
            Both charts show data from FY 2024-25 onward only. Earlier years are excluded. The QoQ chart labels show "FY 2024-25 Q1", "FY 2024-25 Q2", etc.
          </Note>
        </Widget>

        <Widget name="Source of Fund x Portfolio (Heatmap Matrix)">
          <p>Three toggle views for the Trust-Theme matrix:</p>
          <MetricTable rows={[
            ["Grants (toggle)", "SOURCE.TRUST_NAME, SOURCE.THEME", "Number of grants at the intersection of a specific Trust (row) and Theme (column)"],
            ["Allocation (toggle)", "SOURCE.AMOUNT_RECOMMENDED", "Sum of sanctioned amounts at the same Trust x Theme intersection, in Crores"],
            ["AWP (toggle)", "FUNDING_SOURCE_ALLOCATION.ALLOCATED_AMOUNT", "Annual Work Plan allocation from Funding Source Allocation data, grouped by Trust x Theme"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "State (map)", "District (map)"]} />
          <Note>
            When a state/district is selected, grant count and allocation values use geographic apportionment. The AWP toggle pulls from the FUNDING_SOURCE_ALLOCATION table which is independent of grant-level geography.
          </Note>
        </Widget>

        <Widget name="Grants Table (Bottom of Page)">
          <p>A paginated, searchable table listing individual grants. This table responds to all filters and map clicks.</p>
          <MetricTable rows={[
            ["Grant ID", "SOURCE.GRANT_OR_REQUEST", "Unique grant identifier"],
            ["Project Title", "SOURCE.PROJECT_TITLE", "Name of the funded project"],
            ["Grantee", "SOURCE.GRANTEE_NAME", "Organization receiving the grant"],
            ["Portfolio / Theme", "SOURCE.THEME", "Thematic category of the grant"],
            ["Source of Fund", "SOURCE.TRUST_NAME", "Which trust is funding the grant"],
            ["Status", "SOURCE.STATUS", "Current grant status (Ongoing, Active, Closed, etc.)"],
            ["FY", "(Derived from PROJECT_START_DATE)", "Financial year based on project start date"],
            ["Grant Approved Date", "SOURCE.GRANT_APPROVED_DATE", "Date the grant was formally approved"],
            ["Sanctioned (Cr)", "SOURCE.AMOUNT_RECOMMENDED", "Approved grant amount in Crores"],
            ["Disbursed (Cr)", "PAYMENTS.AMOUNT_PAID", "Sum of payments where PAID_DATE falls within the selected FY, in Crores"],
            ["Sanctioned Geography", "(Derived)", "State-level breakdown of sanctioned amounts using apportionment weights"],
            ["Disbursed Geography", "(Derived)", "State-level breakdown of disbursed amounts using apportionment weights"],
            ["Start Date", "SOURCE.PROJECT_START_DATE", "Project start date"],
            ["End Date", "SOURCE.PROJECT_END_DATE", "Project end date"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "State (map)", "District (map)", "Search", "Sort"]} />
          <Note>
            When a state or district is selected on the map, the table filters to grants in that location. The search input clears when filters change. Sanctioned Geography and Disbursed Geography columns show per-state breakdowns with proportional apportionment.
          </Note>
        </Widget>
      </Section>

      {/* ─── FINANCIAL PAGE ─── */}
      <Section title="Financial Page">
        <Widget name="KPI Cards (Top Row)">
          <p>Three KPI cards at the top of the Financial page, in order:</p>
          <MetricTable rows={[
            ["Total AWP", "FUNDING_SOURCE_ALLOCATION.ALLOCATED_AMOUNT", "Sum of Annual Work Plan allocated amounts from the Funding Source Allocation table, filtered by theme if applicable. Displayed first."],
            ["Total Sanctioned", "SOURCE.AMOUNT_RECOMMENDED", "Sum of approved/recommended amounts for all matching grants (sourced from fluxx_grants table)"],
            ["Total Disbursed", "PAYMENTS.AMOUNT_PAID", "Sum of payments where PAYMENT_STATUS = 'Paid' and PAID_DATE falls within the selected FY, for matching grants"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee"]} />
        </Widget>

        <Widget name="Budget Distribution (Heatmap Table)">
          <p>
            A heatmap table showing budget category breakdowns by Theme, with two toggle views:
            <strong> Thematic</strong> (default) groups by theme, <strong>Source of Fund</strong> groups by trust.
          </p>
          <MetricTable rows={[
            ["Admin & Personnel", "GRANT_BUDGETS.ADMIN_PERSONNEL_TATA_TOTAL", "Total budgeted for admin & personnel across all grants in each theme/trust"],
            ["Capital", "GRANT_BUDGETS.CAPITAL_TATA_TOTAL", "Total budgeted for capital expenditure"],
            ["Overhead", "GRANT_BUDGETS.OVERHEAD_TATA_TOTAL", "Total budgeted for overhead costs"],
            ["Personnel", "GRANT_BUDGETS.PERSONNEL_TATA_TOTAL", "Total budgeted for personnel"],
            ["Programme", "GRANT_BUDGETS.PROGRAM_TATA_TOTAL", "Total budgeted for programme activities"],
            ["Total", "(Sum of above 5)", "Grand total of all five budget categories per theme/trust row"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee"]} />
          <Note>
            <strong>Important:</strong> The Budget Distribution total may differ from the Total Sanctioned KPI. This is because some grants (currently 19) do not have a budget breakdown entered in source data's GRANT_BUDGETS table. The KPI reflects all grants' sanctioned amounts, while this table only includes grants with budget data. A footnote below the table explains this.
          </Note>
        </Widget>

        <Widget name="Theme-wise Financial Performance (Heatmap Table)">
          <p>
            A comparison heatmap showing allocation, AWP, disbursement, and utilisation per theme. <strong>Data is sourced from fluxx_grants</strong> (not payments), so all grants are included even if they have zero disbursement.
          </p>
          <MetricTable rows={[
            ["Theme", "SOURCE.THEME", "Thematic category grouping"],
            ["Grants", "SOURCE.GRANT_OR_REQUEST", "Count of distinct grants per theme (sourced from fluxx_grants)"],
            ["Sanctioned (Cr)", "SOURCE.AMOUNT_RECOMMENDED", "Sum of sanctioned amounts for grants in each theme, in Crores"],
            ["AWP (Cr)", "FUNDING_SOURCE_ALLOCATION.ALLOCATED_AMOUNT", "Annual Work Plan allocation from FSA data, grouped by theme"],
            ["Disbursed (Cr)", "PAYMENTS.AMOUNT_PAID", "Sum of payments where status = 'Paid' for grants in that theme"],
            ["Disbursement Rate (%)", "(Derived)", "Disbursed / Sanctioned * 100"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee"]} />
          <Note>
            Column order: Grants → Sanctioned → AWP → Disbursed → Disb%. The grant count and allocation values include all grants from fluxx_grants, even those with zero payments, ensuring the total grants count matches the Executive Overview.
          </Note>
        </Widget>

        <Widget name="Grant Financial Details (Paginated Table)">
          <p>
            A detailed per-grant financial table. <strong>Primary source is fluxx_grants</strong> (not payments), ensuring all grants appear, including those with no payment records.
          </p>
          <MetricTable rows={[
            ["Grant ID", "SOURCE.GRANT_OR_REQUEST", "Grant identifier (without 'G-' prefix)"],
            ["Grantee", "SOURCE.GRANTEE_NAME", "Organization receiving the grant"],
            ["Theme", "SOURCE.THEME", "Thematic category"],
            ["Project Title", "SOURCE.PROJECT_TITLE", "Name of the funded project"],
            ["Allocation (Cr)", "SOURCE.AMOUNT_RECOMMENDED", "Sanctioned amount in Crores (from fluxx_grants)"],
            ["Disbursed (Cr)", "PAYMENTS.AMOUNT_PAID", "Sum of payments for this grant where status = 'Paid', in Crores. Shows 0.00 if no payments exist."],
            ["Utilised (Cr)", "BUDGET_AND_UTILIZATION (actuals)", "Sum of actual expenditure across 5 categories, in Crores"],
            ["Disbursement Rate (%)", "(Derived)", "Disbursed / Allocation * 100"],
            ["Utilised (%)", "(Derived)", "Utilised / Allocation * 100"],
            ["Payments", "PAYMENTS.ID", "Count of payment records for this grant. Shows 0 if no payments exist."],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee"]} />
          <Note>
            The table is paginated (20 rows per page) and sorted by allocation descending. All 583 grants are included regardless of whether they have payment or utilisation data. Grants without payments show 0 for disbursement and 0 for payment count.
          </Note>
        </Widget>
      </Section>

      {/* ─── GRANTS PAGE ─── */}
      <Section title="Grants Page">
        <Widget name="Summary Cards (Top Row)">
          <MetricTable rows={[
            ["Total Grants", "SOURCE.GRANT_OR_REQUEST", "Count of all grants matching the selected FY and filters"],
            ["Total Sanctioned (Cr)", "SOURCE.AMOUNT_RECOMMENDED", "Sum of sanctioned amounts for those grants, in Crores"],
            ["Average Grant Size (Cr)", "(Derived)", "Total Sanctioned divided by Total Grants"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status"]} />
        </Widget>

        <Widget name="Grants Table">
          <p>Full paginated and sortable table of individual grants. Same columns as the Executive Overview table, with server-side sorting and searching.</p>
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "Search", "Sort"]} />
        </Widget>

        <Widget name="Export CSV">
          <p>Downloads all matching grants (ignoring pagination) as a CSV spreadsheet. Includes all visible columns plus:</p>
          <MetricTable rows={[
            ["Geography", "SOURCE.GEOGRAPHY", "Raw geography string from source data"],
            ["Grant Anchor", "SOURCE.PROGRAM_OFFICER_NAME", "Assigned Grant Anchor"],
            ["Finance Manager", "SOURCE.FINANCE_MANAGER", "Assigned finance manager"],
            ["SDG", "SOURCE.SDG", "Sustainable Development Goals mapping"],
            ["DEP Reporting", "SOURCE.DEP_REPORTING", "DEP reporting flag"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "Search"]} />
        </Widget>
      </Section>

      {/* ─── GRANTS OVERVIEW ─── */}
      <Section title="Grants Overview Page">
        <Widget name="Grant Search & Selection">
          <p>A searchable dropdown listing all grants. Selecting a grant loads its complete detail panel alongside an interactive India map.</p>
          <MetricTable rows={[
            ["Dropdown source", "SOURCE.GRANT_OR_REQUEST + PROJECT_TITLE", "All grants are listed with their Grant ID and project title. No limit on the number of grants shown."],
            ["Selected grant", "grant_id URL parameter", "The selected grant's ID is stored in the URL query string for direct linking."],
          ]} />
        </Widget>

        <Widget name="Grant Detail Sidebar">
          <p>Shows comprehensive information for the selected grant:</p>
          <MetricTable rows={[
            ["Header chips", "SOURCE.STATUS, THEME, FY, TRUST_NAME", "Status, theme, FY, and trust shown as inline chips"],
            ["Project Title", "SOURCE.PROJECT_TITLE", "Full project name"],
            ["Grantee", "SOURCE.GRANTEE_NAME", "Organization name"],
            ["Date chips", "SOURCE.GRANT_APPROVED_DATE, PROJECT_START_DATE, PROJECT_END_DATE", "Approved, Start, and End dates shown as compact inline chips with calendar icons"],
            ["Sanctioned (Cr)", "SOURCE.AMOUNT_RECOMMENDED", "Sanctioned amount in Crores"],
            ["Disbursed (Cr)", "PAYMENTS.AMOUNT_PAID", "Sum of paid payments, in Crores, with disbursement %"],
            ["SDG chips", "SOURCE.SDG", "Sustainable Development Goals shown as compact inline chips with colored number circles"],
          ]} />
        </Widget>

        <Widget name="Grant Map (Proportional Allocation)">
          <p>An interactive India map showing the grant's geographic coverage with proportional state-level allocation:</p>
          <MetricTable rows={[
            ["State allocation", "grant_geographies + AMOUNT_RECOMMENDED", "Sanctioned amount split proportionally across covered states (by block count, district count, or equal split)"],
            ["States/Districts toggle", "mapViewMode", "Users can switch between state-level and district-level views. Districts button is now enabled."],
            ["District allocation", "(Derived)", "When in district view, each district's share is further split proportionally from its parent state's allocation, weighted by block count."],
          ]} />
        </Widget>

        <Widget name="Grant Geography Tree (Sidebar)">
          <MetricTable rows={[
            ["State → Districts → Blocks", "grant_geographies", "Expandable tree showing the full geographic hierarchy for the grant. States show proportional allocation amount."],
          ]} />
        </Widget>

        <Widget name="Outreach Section">
          <MetricTable rows={[
            ["Institutions", "OUTREACH.NO_OF_INSTITUTIONS", "Number of institutions reached by the grant"],
            ["User Groups", "OUTREACH.NO_OF_USER_GROUPS", "Number of user groups reached"],
            ["Households (HHs)", "OUTREACH.NO_OF_HOUSEHOLDS", "Number of households reached"],
            ["Individuals", "OUTREACH.NO_OF_INDIVIDUALS", "Number of individuals reached"],
          ]} />
        </Widget>

        <Widget name="MEAS Performance (Project-Level Weighted)">
          <p>Shows weighted project performance calculated from MEAS indicator data for a <strong>single grant</strong>:</p>
          <MetricTable rows={[
            ["Indicator count", "COUNT(*) FROM meas_indicators WHERE fluxx_id = grant", "Total number of MEAS indicator rows for this grant across all months"],
            ["Indicator type chips", "meas_indicators.indicator_type", "Activity, Output, Outcome — distinct counts per type"],
            ["YTD Performance (%)", "SUM(ytd_program_achievement) * 100", "Summed across all indicators for the grant in the latest available month only, where weight > 0"],
            ["PTD Performance (%)", "SUM(ptd_program_achievement) * 100", "Same as YTD but using project-to-date achievement values"],
          ]} />
          <Note>
            <strong>Formula:</strong> Performance = SUM(ytd_program_achievement) × 100, filtered to the latest month and indicators with weight &gt; 0.
            Each indicator's ytd_program_achievement = weight × ytd_ind_achievement (pre-computed in source data).
            If all weights sum to 1.0, the result is the true weighted average.
            Values &gt; 100% mean the project is exceeding targets.
            No FY filter is applied at the MEAS level for a single grant — it always uses the latest month across all FYs.
            Grants where all indicators have weight = 0 will show 0%.
            Color coding: Blue (&gt;100%), Green (96–100%), Amber (81–95%), Red (0–80%), Grey (0%).
          </Note>
        </Widget>
      </Section>

      {/* ─── THEMATIC OVERVIEW ─── */}
      <Section title="Thematic Overview Page">
        <Widget name="Theme Cards & Detail Panel">
          <p>Clicking "View details" on a theme card opens a detail panel. The black "N Grants" chip is <strong>clickable</strong> — it scrolls to the Grants Table at the bottom of the page, filtered by that theme.</p>
          <MetricTable rows={[
            ["Theme card metrics", "SOURCE.THEME, AMOUNT_RECOMMENDED, PAYMENTS", "Each card shows: grant count, sanctioned, disbursed, disbursement rate, and a mini bar chart"],
            ["Detail: KPIs", "SOURCE.AMOUNT_RECOMMENDED, PAYMENTS", "Sanctioned, Disbursed, Avg Grant Size, AWP"],
            ["Detail: Top Grantees", "SOURCE.GRANTEE_NAME, AMOUNT_RECOMMENDED", "Top 5 grantees by sanctioned amount within the theme"],
            ["Detail: Outreach", "OUTREACH", "Institutions, User Groups, Households, Individuals — aggregated across all grants in the theme"],
            ["Detail: MEAS Performance", "SUM(ytd/ptd_program_achievement) * 100", "Aggregated across all grants in the theme, filtered to the latest month where weight > 0. FY filter is applied to the MEAS query — only indicators matching the selected FY are included. Because this sums across multiple grants, values can be much higher than 100%."],
            ["Detail: RAG KPIs", "meas_indicators.ytd_program_achievement", "4 color-coded cards showing grant counts by YTD achievement: Blue (> 100%), Green (96–100%), Amber (81–95%), Red (0–80%). Computed per-grant using latest month data with weight > 0."],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee"]} />
        </Widget>

        <Widget name="Grants Table (Bottom of Page)">
          <p>Appears when a theme is selected. Clickable via the black "N Grants" chip in the detail panel header. Shows all grants for the selected theme with search, sort, pagination, and CSV export.</p>
          <FilterList filters={["FY", "View Mode", "Theme (selected)", "Trust", "Status", "Grantee"]} />
        </Widget>
      </Section>

      {/* ─── GEOGRAPHIC OVERVIEW ─── */}
      <Section title="Geographic Overview Page">
        <Widget name="Geographic Summary KPIs">
          <MetricTable rows={[
            ["States & UTs covered", "grant_geographies.state", "Count of unique states/UTs where filtered grants operate (Pan-India grants excluded from count)"],
            ["Districts covered", "grant_geographies.district", "Count of unique districts"],
            ["Blocks covered", "grant_geographies.block", "Count of unique blocks"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "State", "District"]} />
        </Widget>

        <Widget name="State-Level Data (Map & Table)">
          <p>Each state row/tile shows:</p>
          <MetricTable rows={[
            ["Grants in state", "grant_geographies.state", "Number of grants with geography entries in the state"],
            ["Grantees in state", "SOURCE.GRANTEE_NAME", "Number of unique grantee organizations operating in the state"],
            ["Allocation (Cr)", "SOURCE.AMOUNT_RECOMMENDED (proportional)", "Proportional share of sanctioned amount attributed to the state"],
            ["Disbursement (Cr)", "PAYMENTS.AMOUNT_PAID (proportional)", "Same proportional weight applied to disbursement"],
            ["Districts & Blocks", "grant_geographies", "Count of distinct districts and blocks within the state"],
            ["Aspirational Districts", "aspirational_blocks.district_name", "Count of covered districts matching the Aspirational Districts list"],
            ["Top Themes", "SOURCE.THEME", "Themes ranked by allocation within the state"],
          ]} />
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee"]} />
        </Widget>

        <Widget name="How Multi-State Grant Amounts Are Split">
          <p>See the <strong>Geographic Apportionment</strong> section under Global Filters above for the full proportional splitting methodology.</p>
        </Widget>

        <Widget name="Detail Panel Layout">
          <p>The Geographic detail panel uses a <strong>2:3 layout</strong> (compact map on the left, larger detail panel on the right), matching the Grants Overview page style. Contains:</p>
          <MetricTable rows={[
            ["KPIs", "SOURCE.AMOUNT_RECOMMENDED, PAYMENTS", "Sanctioned, Disbursed (with %), Avg Grant Size, AWP — all with proportional apportionment"],
            ["Thematic Coverage", "SOURCE.THEME", "Theme chips with dot colors and grant counts per theme within the geography"],
            ["Outreach", "OUTREACH", "Institutions, User Groups, Households, Individuals aggregated for the geography"],
            ["MEAS Performance", "SUM(ytd/ptd_program_achievement) * 100", "Aggregated across all grants in the geography (state or district), filtered to the latest month where weight > 0. FY filter is applied — only indicators matching the selected FY are included. Scoped by the fluxx_ids of grants that have blocks/districts in the selected geography."],
            ["RAG KPIs", "meas_indicators.ytd_program_achievement", "4 color-coded cards showing grant counts by YTD achievement: Blue (> 100%), Green (96–100%), Amber (81–95%), Red (0–80%). Same thresholds as Thematic page."],
            ["Top Grantees", "SOURCE.GRANTEE_NAME, AMOUNT_RECOMMENDED", "Top grantees by sanctioned amount (moved to bottom, below MEAS)"],
          ]} />
        </Widget>

        <Widget name="Grants Table (Bottom of Page)">
          <p>Appears when a state or district is selected. Clickable via the black "N Grants" chip in the detail panel header. Filters by both <strong>state and district</strong> when drilling into a district.</p>
          <FilterList filters={["FY", "View Mode", "Theme", "Trust", "Status", "Grantee", "State (selected)", "District (selected)"]} />
        </Widget>
      </Section>

      {/* ─── DATA SOURCES ─── */}
      <Section title="source data Data Sources & Column Reference">
        <Widget name="Where Does the Data Come From?">
          <p>
            All data originates from local source files and is loaded into the dashboard's PostgreSQL cache.
            On first startup, if the cache is empty, data is pulled from source data. After that, the dashboard serves from the cache.
            An <strong>automatic sync runs daily at 11:00 PM IST</strong>. A manual refresh can also be triggered via the API.
          </p>
        </Widget>

        <Widget name="Grants Master Table">
          <p className="text-[12px] text-slate-500 mb-1">Source: <span className="font-mono text-blue-700">INPUT.GRANTS</span> &nbsp;|&nbsp; ~583 grants (filtered to G-* only)</p>
          <MetricTable rows={[
            ["Grant ID", "GRANT_OR_REQUEST", "Unique grant identifier (e.g., 'G-2025-73214'). Only records starting with 'G-' are imported; Requests (R-) are excluded."],
            ["Project Title", "PROJECT_TITLE", "Full project name"],
            ["Theme / Portfolio", "THEME", "Thematic category (e.g., Health, Education)"],
            ["Sub-Theme", "SUB_THEME", "Sub-category within a theme"],
            ["Status", "STATUS", "Grant status (Ongoing, Active, Closed, etc.)"],
            ["Grantee Name", "GRANTEE_NAME", "Organization receiving the grant"],
            ["Trust / Source of Fund", "TRUST_NAME", "Funding trust (e.g., Fund A, Fund C)"],
            ["Sanctioned Amount", "AMOUNT_RECOMMENDED", "Approved grant amount in INR"],
            ["Approval Date", "GRANT_APPROVED_DATE", "Date the grant was formally approved"],
            ["Project Start Date", "PROJECT_START_DATE", "Project commencement date (used to derive FY)"],
            ["Project End Date", "PROJECT_END_DATE", "Used for 'All Grants' view mode date overlap"],
            ["Closure Date", "GRANT_CLOSURE_DATE", "Date the grant was formally closed"],
            ["Geography", "GEOGRAPHY", "Raw geography string — parsed during sync into state/district/block entries"],
            ["Grant Anchor", "PROGRAM_OFFICER_NAME", "Assigned Grant Anchor"],
            ["FY (derived)", "(Computed from PROJECT_START_DATE)", "Not a source data column. Calculated during sync based on project start date."],
          ]} />
        </Widget>

        <Widget name="Payments (Disbursement Table)">
          <p className="text-[12px] text-slate-500 mb-1">Source: <span className="font-mono text-blue-700">INPUT.PAYMENTS</span> &nbsp;|&nbsp; ~2,364 rows after dedup (from ~9,216 raw rows)</p>
          <MetricTable rows={[
            ["Grant ID", "GRANT_ID", "Grant identifier without the 'G-' prefix. Linked via BASE_REQUEST_ID."],
            ["Payment ID", "PAYMENT_ID", "Unique payment identifier (used for deduplication)"],
            ["Amount Paid", "AMOUNT_PAID", "Amount disbursed in INR"],
            ["Paid Date", "PAID_DATE", "Date payment was made (used for FY scoping of disbursement)"],
            ["Payment Status", "PAYMENT_STATUS", "Only 'paid' status is counted in disbursement"],
          ]} />
        </Widget>

        <Widget name="Budget Details">
          <p className="text-[12px] text-slate-500 mb-1">Source: <span className="font-mono text-blue-700">INPUT.GRANT_BUDGETS</span> &nbsp;|&nbsp; ~18,077 rows after dedup (from ~70,317 raw rows)</p>
          <MetricTable rows={[
            ["Grant ID", "GRANT_ID", "Linked to grants via BASE_REQUEST_ID"],
            ["Budget Particulars", "BUDGET_PARTICULARS", "Description of the budget line item"],
            ["5 Category Totals", "ADMIN_PERSONNEL/CAPITAL/OVERHEAD/PERSONNEL/PROGRAM_TATA_TOTAL", "Budgeted amounts per category"],
            ["Total Budget", "(Sum of above 5)", "Computed as sum of all five budget category columns"],
          ]} />
          <Note>
            19 grants currently have sanctioned amounts but no budget breakdown entries in this table. This causes a ~259 Cr gap between the Total Sanctioned KPI and the Budget Distribution heatmap total.
          </Note>
        </Widget>

        <Widget name="Utilisation Details">
          <p className="text-[12px] text-slate-500 mb-1">Source: <span className="font-mono text-blue-700">INPUT.BUDGET_AND_UTILIZATION</span> &nbsp;|&nbsp; ~30,739 rows after dedup (from ~115,757 raw rows)</p>
          <MetricTable rows={[
            ["Grant ID", "GRANT_ID", "Linked to grants"],
            ["Report ID / Status", "REPORT_ID, REPORT_STATUS", "Utilisation report tracking"],
            ["5 Categories (Budget)", "ADMIN_PERSONNEL/OVERHEAD/CAPITAL/PERSONNEL/PROGRAMME_TATA_BUDGET", "Budgeted amounts per category"],
            ["5 Categories (Actuals)", "ADMIN_PERSONNEL/OVERHEAD/CAPITAL/PERSONNEL/PROGRAMME_TATA_ACTUALS", "Actual expenditure per category"],
          ]} />
        </Widget>

        <Widget name="AWP / Funding Source Allocation (FSA)">
          <p className="text-[12px] text-slate-500 mb-1">Source: <span className="font-mono text-blue-700">INPUT.FUNDING_SOURCE_ALLOCATION</span> &nbsp;|&nbsp; 140 rows</p>
          <MetricTable rows={[
            ["Funding Source", "FUNDING_SOURCE_NAME", "Name of the funding source"],
            ["Theme", "THEME_NAME", "Thematic category"],
            ["Allocated Amount", "ALLOCATED_AMOUNT", "AWP allocated amount in INR"],
            ["Spending Year", "SPENDING_YEAR", "Year the allocation applies to"],
            ["Trust", "TRUST_ACRONYM, TRUST_NAME", "Funding trust details"],
          ]} />
        </Widget>

        <Widget name="Outreach">
          <p className="text-[12px] text-slate-500 mb-1">Source: <span className="font-mono text-blue-700">INPUT.OUTREACH</span> &nbsp;|&nbsp; 157 rows</p>
          <MetricTable rows={[
            ["Base Request", "BASE_REQUEST", "Linked to grant"],
            ["Geography", "GEOGRAPHY", "Geographic coverage"],
            ["Individuals / Institutions / Households", "NO_OF_INDIVIDUALS, NO_OF_INSTITUTIONS, NO_OF_HOUSEHOLDS", "Outreach counts by type"],
          ]} />
        </Widget>

        <Widget name="Grant Geographies (Parsed from grants)">
          <p className="text-[12px] text-slate-500 mb-1">Not a direct source data table. Parsed from <span className="font-mono text-blue-700">SOURCE.GEOGRAPHY</span> during sync.</p>
          <MetricTable rows={[
            ["Grant ID", "GRANT_OR_REQUEST", "Links back to the master grant record"],
            ["State / District / Block", "state, district, block", "Parsed geographic hierarchy"],
            ["Pan-India flag", "is_pan_india", "TRUE for grants tagged as covering all of India"],
          ]} />
        </Widget>

        <Widget name="Aspirational Blocks (Reference Data)">
          <p className="text-[12px] text-slate-500 mb-1">Not from source data. Seeded from a local reference file. 499 rows.</p>
          <MetricTable rows={[
            ["State / District / Block", "state_name, district_name, block_name", "112 Aspirational Districts and 499 Aspirational Blocks as identified by Government of India"],
          ]} />
        </Widget>

        <Widget name="Data Refresh & Deduplication Summary">
          <MetricTable rows={[
            ["Refresh frequency", "--", "Daily at 11:00 PM IST (automatic via APScheduler) + on first startup if cache is empty"],
            ["Manual refresh", "POST /api/etl/refresh", "Triggers an immediate source data-to-Postgres sync"],
            ["Grants", "INPUT.GRANTS", "~583 grants (G-* only, Requests excluded)"],
            ["Payments", "INPUT.PAYMENTS", "~2,364 rows after dedup by PAYMENT_ID (from ~9,216 raw)"],
            ["Budget", "INPUT.GRANT_BUDGETS", "~18,077 rows after exact-row dedup (from ~70,317 raw)"],
            ["Utilisation", "INPUT.BUDGET_AND_UTILIZATION", "~30,739 rows after exact-row dedup (from ~115,757 raw)"],
            ["AWP/FSA", "INPUT.FUNDING_SOURCE_ALLOCATION", "140 rows"],
            ["Outreach", "INPUT.OUTREACH", "157 rows"],
          ]} />
        </Widget>
      </Section>

      {/* ─── HARDCODED VALUES ─── */}
      <Section title="Hardcoded Values & Overrides">
        <Widget name="Budget vs Actual Table (Financial Page)">
          <p>
            A completely <strong>static, hardcoded</strong> table on the Financial page showing Budget vs Actual for FY 2025-26.
            This table does not change with any filters. All values are fixed and provided by the client. Currently hidden per user request.
          </p>
          <MetricTable rows={[
            ["Sections", "(Static)", "Major Themes, Individual Grants (IGP), Other (AI 171 Memorial), with sub-totals and grand totals"],
            ["Budget columns", "(Static)", "Committed Expenditure, New Grant Proposals, Total Projected Spends"],
            ["Actual columns", "(Static)", "Committed Expenditure, New Grant, Total"],
            ["Grand Total Budget", "(Static)", "Rs 1,585.84 Cr"],
            ["Grand Total Actual", "(Static)", "Rs 1,586.07 Cr"],
          ]} />
          <Note>
            Education and Individual Grants sections are collapsible (collapsed by default). To update these values, edit <strong>/app/frontend/src/components/BudgetVsActualTable.jsx</strong>.
          </Note>
        </Widget>

        <Widget name="Balance Commitment (Executive Overview)">
          <p>
            The "Balance Commitment" KPI card on the Executive Overview displays a <strong>hardcoded value of Rs 929.00 Cr</strong>.
            This is not derived from the database.
          </p>
          <p>
            <strong>Cumulative Balance Commitment</strong> = Pending Disbursement + 929 Cr (where Pending Disbursement = Sanctioned - Disbursement Cumulative).
          </p>
        </Widget>

        <Widget name="Grant Start Date Overrides (Post-ETL)">
          <p>
            After every source data sync, the following grants have their <strong>project_start_date overridden to their grant_approved_date</strong>, and the FY column updated accordingly.
            This runs automatically via <strong>/app/backend/grant_date_overrides.py</strong>.
          </p>
          <MetricTable rows={[
            ["G-2025-70517", "start_date → 2025-03-20 (approved date)", "FY overridden to FY 2024-25"],
            ["G-2025-73032", "start_date → 2025-03-20 (approved date)", "FY overridden to FY 2024-25"],
            ["G-2025-73375", "start_date → 2025-03-13 (approved date)", "FY overridden to FY 2024-25"],
            ["G-2025-73400", "start_date → 2025-03-11 (approved date)", "FY overridden to FY 2024-25"],
            ["G-2026-74513", "start_date → 2026-03-23 (approved date)", "FY overridden to FY 2025-26"],
            ["G-2026-74515", "start_date → 2026-03-23 (approved date)", "FY overridden to FY 2025-26"],
          ]} />
          <Note>
            These overrides persist across ETL refreshes (startup, scheduled, manual). The FY is also overridden because the fy column is loaded from source data and would otherwise be stale. March dates fall in the previous FY (Indian FY runs April-March).
          </Note>
        </Widget>

        <Widget name="Force Ongoing Overrides (View Mode)">
          <p>
            The following 7 grants are <strong>excluded from "New" and forced into "Ongoing"</strong> for FY 2025-26, even though their start dates fall within FY 2025-26.
            This is configured in <strong>/app/backend/grant_filter_utils.py</strong> (FORCE_ONGOING_OVERRIDES).
          </p>
          <MetricTable rows={[
            ["G-2026-73883", "FY 2025-26", "Forced Ongoing (excluded from New)"],
            ["G-2025-73450", "FY 2025-26", "Forced Ongoing (excluded from New)"],
            ["G-2026-73535", "FY 2025-26", "Forced Ongoing (excluded from New)"],
            ["G-2026-73632", "FY 2025-26", "Forced Ongoing (excluded from New)"],
            ["G-2026-73766", "FY 2025-26", "Forced Ongoing (excluded from New)"],
            ["G-2026-74513", "FY 2025-26", "Forced Ongoing (excluded from New)"],
            ["G-2026-74515", "FY 2025-26", "Forced Ongoing (excluded from New)"],
          ]} />
          <Note>
            Their sanctioned and disbursement amounts are counted under Ongoing instead of New for FY 2025-26. This applies across all pages.
          </Note>
        </Widget>

        <Widget name="Payment Overrides (Post-ETL)">
          <p>
            <strong>Payment date fixes:</strong> The following refunded payments had NULL paid_date in source data. After ETL, their paid_date is set so they are included in disbursement calculations (reducing totals since amounts are negative).
          </p>
          <MetricTable rows={[
            ["Payment 92745", "Grant 70106 | Amount: -Rs 1.59 Cr | Refunded", "paid_date set to 2019-06-03 (FY 2019-20)"],
            ["Payment 96440", "Grant 70029 | Amount: -Rs 0.03 Cr | Refunded", "paid_date set to 2024-03-05 (FY 2023-24)"],
          ]} />
          <p className="mt-3">
            <strong>Excluded payments:</strong> The following payments are <strong>deleted</strong> from the payments table after every ETL sync to exclude them from all disbursement calculations.
          </p>
          <MetricTable rows={[
            ["Payment 91012", "Grant 70207 | Amount: Rs 30.46 Lakh | Paid", "Excluded — deleted after every ETL sync"],
          ]} />
          <Note>
            All payment overrides are managed in <strong>/app/backend/grant_date_overrides.py</strong>.
          </Note>
        </Widget>

        <Widget name="Geography State Overrides (Post-ETL)">
          <p>
            After every source data sync, the following grants have their <strong>state mapping corrected</strong> in the grant_geographies table.
          </p>
          <MetricTable rows={[
            ["G-2025-73462", "GoSports Foundation (Education)", "State changed from 'Not Applicable' to 'Karnataka'"],
          ]} />
          <Note>
            Managed in <strong>/app/backend/grant_date_overrides.py</strong> (GEO_STATE_OVERRIDES).
          </Note>
        </Widget>

        <Widget name="AWP Redistribution — New Initiative (Post-ETL)">
          <p>
            After every source data sync, AWP rows with theme "New Initiative" for FY 2025-26 (spending_year=2026) are <strong>deleted and redistributed</strong> across other themes.
            This runs via <strong>/app/backend/new_initiative_redistribution.py</strong>.
          </p>
          <MetricTable rows={[
            ["Cancer Care carve-out", "(Hardcoded)", "Fund A = 90 Cr, Fund B = 10 Cr"],
            ["Discretionary carve-out", "(Hardcoded)", "Fund A = 40 Cr, Fund B = 50 Cr, Fund C = 10 Cr"],
            ["Theme proportional split", "(Hardcoded)", "Remaining amount distributed proportionally across Rural Upliftment, Education, Healthcare, Water & Sanitation, Nutrition, Urban Poverty Alleviation, Arts, and Institutions"],
          ]} />
          <Note>
            All AWP rows for FY 2024-25 (spending_year=2025) are also deleted entirely. "Discretionary" is a synthetic theme added by this redistribution.
          </Note>
        </Widget>

        <Widget name="Default FY Selection">
          <p>
            The dashboard defaults to <strong>FY 2025-26</strong> on initial load (not "All Years"). The "Clear Filters" button also resets to FY 2025-26.
            This is configured in <strong>/app/frontend/src/utils/dashboardFilters.js</strong>.
          </p>
        </Widget>

        <Widget name="Disbursement Cumulative Logic">
          <p>
            The <strong>Disbursement (Cumulative)</strong> KPI is capped at 31st March of the selected FY (not all-time).
            If multiple FYs are selected, it uses the latest FY's end date (31st March).
          </p>
          <MetricTable rows={[
            ["FY 2024-25 selected", "paid_date <= 2025-03-31", "Cumulative as on 31 Mar 2025"],
            ["FY 2025-26 selected", "paid_date <= 2026-03-31", "Cumulative as on 31 Mar 2026"],
            ["Multiple FYs selected", "paid_date <= latest 31 Mar", "Uses the latest FY end date"],
          ]} />
        </Widget>
      </Section>
    </div>
  );
}
